import React, { useEffect, useRef, useState } from 'react'
import {
  PHONE_DISPLAY,
  PHONE_HREF,
  ADDRESS_LINE1,
  ADDRESS_LINE2,
  ADDRESS_ONE_LINE,
  ADDRESS_HEADLINE,
} from '../../config/business';
import LandingHeader from '../../components/LandingHeader/LandingHeader'
import LandingFooter from '../../components/LandingFooter/LandingFooter'
import Turnstile from '../../components/Turnstile'
import Seo from '../../components/Seo/Seo'
import { captureLead, LEAD_SEGMENTS } from '../../utils/leadCapture'
import { notifyLead } from '../../utils/leadNotify'
import './Contact.css'

const MAROON = '#8A2233'
const INK = '#1A1917'
const DARK = '#17161A'
const CREAM = '#FAF9F7'
const EXP = "'Archivo Expanded','Archivo',sans-serif"


export default function Contact() {
  const [form, setForm] = useState({
    name: '', email: '', org: '', role: 'Veterinarian', subject: '', message: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [captchaToken, setCaptchaToken] = useState('')
  const turnstileRef = useRef(null)
  const fallbackRef = useRef(null)
  const errorRef = useRef(null)

  // Move focus to the error so keyboard / screen-reader users are taken to it
  // on a failed submit (WCAG 3.3.1), rather than only hearing the alert.
  useEffect(() => {
    if (error) errorRef.current?.focus()
  }, [error])

  const mapContainerRef = useRef(null)
  const mapRef = useRef(null)
  const [mapState, setMapState] = useState({ status: 'idle', message: '' })
  const mapboxToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN

  useEffect(() => {
    const els = document.querySelectorAll('.q-reveal')
    if (!('IntersectionObserver' in window)) {
      els.forEach((e) => e.classList.add('q-in'))
      return
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('q-in')
            io.unobserve(en.target)
          }
        }),
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    )
    els.forEach((e) => io.observe(e))
    fallbackRef.current = setTimeout(() => els.forEach((e) => e.classList.add('q-in')), 1600)
    return () => {
      io.disconnect()
      clearTimeout(fallbackRef.current)
    }
  }, [])

  useEffect(() => {
    const container = mapContainerRef.current
    if (!container) return () => {}

    if (!mapboxToken) {
      setMapState({
        status: 'no-token',
        message: 'Mapbox token is not configured.',
      })
      return () => {}
    }

    let cancelled = false
    setMapState({ status: 'loading', message: 'Loading map…' })

    ;(async () => {
      try {
        const [mapboxModule] = await Promise.all([
          import('mapbox-gl'),
          import('mapbox-gl/dist/mapbox-gl.css'),
        ])
        const mapboxgl = mapboxModule.default ?? mapboxModule

        const geocodeUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          ADDRESS_ONE_LINE
        )}.json?access_token=${encodeURIComponent(mapboxToken)}&limit=1`

        const res = await fetch(geocodeUrl)
        if (!res.ok) {
          throw new Error(`Geocoding failed (${res.status})`)
        }
        const data = await res.json()
        const feature = data?.features?.[0]
        const center = feature?.center
        if (!Array.isArray(center) || center.length < 2) {
          throw new Error('No geocode results for address')
        }
        const [lng, lat] = center

        if (cancelled) return

        mapboxgl.accessToken = mapboxToken
        const map = new mapboxgl.Map({
          container,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [lng, lat],
          zoom: 13.5,
        })

        mapRef.current = map
        map.scrollZoom.disable()
        map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')

        const popup = new mapboxgl.Popup({ offset: 22 }).setHTML(
          `<div style="font-weight:700;margin-bottom:4px;">Qentoros</div><div style="font-size:12px;opacity:0.9;">${ADDRESS_ONE_LINE}</div>`
        )

        new mapboxgl.Marker({ color: MAROON })
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(map)

        map.once('load', () => {
          if (!cancelled) setMapState({ status: 'ready', message: '' })
        })
      } catch (err) {
        if (cancelled) return
        setMapState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Failed to load map.',
        })
      }
    })()

    return () => {
      cancelled = true
      try {
        mapRef.current?.remove()
      } catch {
        // ignore cleanup errors
      } finally {
        mapRef.current = null
      }
    }
  }, [mapboxToken])

  const onField = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }))
    if (error) setError('')
  }
  const onSubmit = async (e) => {
    e.preventDefault()
    if (submitting) return
    // Validate on submit (button stays enabled) so the reason is announced
    // rather than the control being silently un-clickable.
    if (!captchaToken) {
      setError('Please complete the verification below, then send your message.')
      return
    }
    setSubmitting(true)
    setError('')

    const { error: captureError } = await captureLead({
      email: form.email,
      segment: LEAD_SEGMENTS.CONTACT,
      source: 'contact',
      name: form.name,
      payload: {
        org: form.org,
        role: form.role,
        subject: form.subject,
        message: form.message,
      },
      token: captchaToken,
    })

    // The Turnstile token is single-use; clear it whatever the outcome.
    turnstileRef.current?.reset()
    setCaptchaToken('')
    setSubmitting(false)

    if (captureError) {
      setError("Something went wrong sending your message. Please try again.")
      return
    }

    // Auto-reply to the visitor and alert the team. Fire-and-forget: the enquiry
    // is already saved, so a mail hiccup must not block the success screen.
    notifyLead({ email: form.email, segment: LEAD_SEGMENTS.CONTACT })

    setSubmitted(true)
  }

  return (
    <div
      style={{
        background: CREAM,
        color: INK,
        // `clip` (not `hidden`) prevents horizontal overflow WITHOUT making this
        // wrapper a vertical scroll container. `overflow-x: hidden` forces
        // `overflow-y` to compute to `auto`, nesting a scroll container on top of
        // the document scroll — which spawns phantom blank space below the footer
        // under aggressive/momentum scrolling.
        overflowX: 'clip',
        fontFamily: "'Archivo',sans-serif",
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Seo
        title="Contact Qentoros"
        description="Get in touch with Qentoros about BIO-PLY™ clinical use, partnerships, or supply for equine joint sepsis, synovitis, and soft-tissue healing."
        path="/contact"
      />
      {/* Shared site header/navigation */}
      <LandingHeader />

      {/* Grows to fill any viewport taller than the content so the footer
          stays pinned to the bottom instead of leaving empty scrollable
          space beneath it. */}
      <main id="main" tabIndex={-1} style={{ flex: '1 0 auto' }}>
      {/* HEADER */}
      <section className="q-contact-x" style={{ padding: 'clamp(96px,11vw,132px) 7vw clamp(36px,4.5vw,60px)', background: CREAM }}>
        {/* Above the fold: self-playing CSS entrance (not observer-driven) so
            the prerendered hero is visible on first paint before the JS bundle. */}
        <div className="q-reveal-onload" style={{ maxWidth: '1180px', margin: '0 auto' }}>
          <p style={eyebrow(MAROON)}>Get in touch</p>
          <h1
            style={{
              fontFamily: EXP,
              fontWeight: 700,
              fontSize: 'clamp(36px,5vw,68px)',
              lineHeight: 1.02,
              letterSpacing: '-.025em',
              color: INK,
              marginBottom: '18px',
            }}
          >
            Let's start a <span style={{ color: MAROON }}>conversation</span>
          </h1>
          <p style={{ fontSize: 'clamp(17px,1.6vw,20px)', lineHeight: 1.55, color: '#5C5853', maxWidth: '620px' }}>
            Whether you're a veterinarian, a horse owner, an investor, or press, tell us
            a little about you and the right person at Qentoros will follow up.
          </p>
        </div>
      </section>

      {/* BODY */}
      <section id="form" className="q-contact-x" style={{ padding: 'clamp(8px,1.5vw,20px) 7vw clamp(48px,6vw,76px)', background: CREAM }}>
        <div
          className="q-grid-2"
          style={{
            maxWidth: '1180px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: '1.25fr .75fr',
            gap: 'clamp(22px,2.6vw,36px)',
            alignItems: 'start',
          }}
        >
          {/* FORM CARD */}
          <div
            className="q-reveal q-form-card"
            style={{ background: '#fff', border: '1px solid #E9E4DC', borderRadius: '18px', padding: 'clamp(26px,3vw,44px)', boxShadow: '0 1px 2px rgba(26,25,23,.04)' }}
          >
            {submitted ? (
              <div style={{ textAlign: 'center', padding: 'clamp(26px,4vw,48px) 10px' }}>
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: MAROON,
                    margin: '0 auto 22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '27px',
                    color: '#fff',
                  }}
                >
                  ✓
                </div>
                <h3 style={{ fontFamily: EXP, fontWeight: 700, fontSize: '25px', color: INK, marginBottom: '10px', letterSpacing: '-.01em' }}>
                  Thanks, your message is on its way.
                </h3>
                <p style={{ fontSize: '16px', color: '#6E6A65', lineHeight: 1.55, maxWidth: '380px', margin: '0 auto' }}>
                  A member of the Qentoros team will be in touch within two business days.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} style={{ display: 'grid', gap: '16px' }}>
                <div className="q-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label htmlFor="contact-name" style={labelStyle}>Name</label>
                    <input id="contact-name" name="name" autoComplete="name" value={form.name} onChange={onField} placeholder="Your name" style={fieldStyle} />
                  </div>
                  <div>
                    <label htmlFor="contact-email" style={labelStyle}>Email</label>
                    <input id="contact-email" name="email" type="email" autoComplete="email" value={form.email} onChange={onField} placeholder="you@clinic.com" style={fieldStyle} />
                  </div>
                </div>
                <div className="q-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label htmlFor="contact-org" style={labelStyle}>
                      Organization <span style={{ color: '#9A958F', fontWeight: 400 }}>(optional)</span>
                    </label>
                    <input id="contact-org" name="org" autoComplete="organization" value={form.org} onChange={onField} placeholder="Clinic, farm, or firm" style={fieldStyle} />
                  </div>
                  <div>
                    <label htmlFor="contact-role" style={labelStyle}>I am a…</label>
                    <select id="contact-role" name="role" value={form.role} onChange={onField} style={fieldStyle}>
                      <option value="Veterinarian">Veterinarian</option>
                      <option value="Horse owner">Horse owner</option>
                      <option value="Investor">Investor</option>
                      <option value="Press">Press / media</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" style={labelStyle}>Subject</label>
                  <input id="contact-subject" name="subject" value={form.subject} onChange={onField} placeholder="What's this about?" style={fieldStyle} />
                </div>
                <div>
                  <label htmlFor="contact-message" style={labelStyle}>Message</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={form.message}
                    onChange={onField}
                    rows={5}
                    placeholder="Tell us what you'd like to know or discuss…"
                    style={{ ...fieldStyle, resize: 'vertical' }}
                  />
                </div>
                <div className="q-submit-row" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginTop: '4px' }}>
                  <button
                    type="submit"
                    className="q-cta"
                    disabled={submitting}
                    aria-describedby="contact-verify-hint"
                    style={{
                      background: MAROON,
                      color: '#fff',
                      border: `1px solid ${MAROON}`,
                      fontSize: '15px',
                      fontWeight: 600,
                      padding: '15px 30px',
                      borderRadius: '10px',
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      opacity: submitting ? 0.6 : 1,
                      fontFamily: "'Archivo',sans-serif",
                      transition: 'all .25s ease',
                      flex: 'none',
                    }}
                  >
                    {submitting ? 'Sending…' : 'Send message'}
                  </button>
                  <Turnstile
                    ref={turnstileRef}
                    onVerify={setCaptchaToken}
                    onExpire={() => setCaptchaToken('')}
                    onError={() => setCaptchaToken('')}
                  />
                </div>
                <p id="contact-verify-hint" style={{ margin: 0, fontSize: '13px', color: '#6E6A65' }}>
                  Complete the verification above before sending.
                </p>
                {error && (
                  <p ref={errorRef} tabIndex={-1} role="alert" style={{ margin: 0, fontSize: '14px', color: '#B42318', outline: 'none' }}>
                    {error}
                  </p>
                )}
              </form>
            )}
          </div>

          {/* INFO COLUMN */}
          <div className="q-reveal" style={{ display: 'grid', gap: '16px' }}>
            <div style={{ background: DARK, color: '#F5F3F0', borderRadius: '18px', padding: 'clamp(24px,2.6vw,32px)' }}>
              <p style={{ fontSize: '12px', letterSpacing: '.16em', textTransform: 'uppercase', color: '#D9899A', fontWeight: 700, marginBottom: '22px' }}>
                Reach us directly
              </p>
              <div style={{ display: 'grid', gap: '22px' }}>
                {[
                  ['Message', <a key="e" href="#form" style={{ fontSize: '17px', color: '#F5F3F0', textDecoration: 'none', fontWeight: 500 }}>Use the form on this page</a>],
                  ['Phone', <a key="p" href={PHONE_HREF} style={{ fontSize: '17px', color: '#F5F3F0', textDecoration: 'none', fontWeight: 500 }}>{PHONE_DISPLAY}</a>],
                  ['Office', <div key="o" style={{ fontSize: '16px', color: '#CFCBC7', lineHeight: 1.5 }}>{ADDRESS_LINE1}<br />{ADDRESS_LINE2}</div>],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div style={{ fontSize: '11.5px', letterSpacing: '.12em', textTransform: 'uppercase', color: '#8E8A86', fontWeight: 600, marginBottom: '6px' }}>
                      {label}
                    </div>
                    {value}
                  </div>
                ))}
              </div>
              <div style={{ height: '1px', background: 'rgba(245,243,240,.14)', margin: '24px 0' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#E79A8C', flex: 'none' }} />
                <span style={{ fontSize: '14px', color: '#CFCBC7', lineHeight: 1.5 }}>
                  We typically respond within <strong style={{ color: '#F5F3F0', fontWeight: 600 }}>2 business days</strong>.
                </span>
              </div>
            </div>

            <div style={{ background: '#fff', border: '1px solid #E9E4DC', borderRadius: '18px', padding: 'clamp(24px,2.6vw,32px)' }}>
              <p style={{ fontSize: '12px', letterSpacing: '.16em', textTransform: 'uppercase', color: MAROON, fontWeight: 700, marginBottom: '8px' }}>
                What happens next
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {[
                  ['01', 'We review your message and route it to the right team.'],
                  ['02', 'The right person reaches out, usually within two business days.'],
                  ['03', 'We share trial details, availability, or your next steps.'],
                ].map(([num, body]) => (
                  <div key={num} style={{ display: 'flex', gap: '14px', padding: '16px 0', borderTop: '1px solid #EFEAE2' }}>
                    <span style={{ fontFamily: EXP, fontSize: '14px', color: MAROON, fontWeight: 700, lineHeight: 1.5, flex: 'none' }}>{num}</span>
                    <div style={{ fontSize: '14.5px', color: '#4A4744', lineHeight: 1.5 }}>{body}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="q-contact-x" style={{ padding: '0 7vw clamp(80px,10vw,128px)', background: CREAM }}>
        <div className="q-reveal" style={{ maxWidth: '1180px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px', marginBottom: '22px' }}>
            <div>
              <p style={{ ...eyebrow(MAROON), marginBottom: '12px' }}>Find us</p>
              <h2 style={{ fontFamily: EXP, fontWeight: 700, fontSize: 'clamp(24px,3vw,38px)', lineHeight: 1.06, letterSpacing: '-.02em', color: INK }}>
                {ADDRESS_HEADLINE}
              </h2>
            </div>
            <a
              href="https://www.google.com/maps/search/?api=1&query=2903%20Commerce%20Street%2C%20Blacksburg%2C%20VA%2024060"
              target="_blank"
              rel="noopener"
              className="q-link"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', color: INK, textDecoration: 'none', fontSize: '15px', fontWeight: 600, transition: 'gap .25s ease' }}
            >
              Open in Google Maps <span className="q-arrow" style={{ transition: 'transform .25s ease' }}>→</span>
            </a>
          </div>
          <div style={{ position: 'relative', borderRadius: '18px', overflow: 'hidden', border: '1px solid #E9E4DC', boxShadow: '0 1px 2px rgba(26,25,23,.04)', height: 'clamp(300px,40vw,440px)', background: '#EDE8E1' }}>
            <div ref={mapContainerRef} style={{ position: 'absolute', inset: 0 }} />

            {(mapState.status === 'no-token' || mapState.status === 'error') && (
              <div style={mapFallback}>
                <div style={{ maxWidth: '360px' }}>
                  <div style={{ fontFamily: EXP, fontWeight: 700, fontSize: '18px', color: INK, marginBottom: '8px' }}>
                    Map preview unavailable
                  </div>
                  <p style={{ fontSize: '14.5px', color: '#5C5853', lineHeight: 1.5, marginBottom: '18px' }}>
                    {mapState.status === 'no-token'
                      ? 'Add a Mapbox token to enable the interactive map.'
                      : mapState.message || 'There was a problem loading the map.'}
                  </p>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=2903%20Commerce%20Street%2C%20Blacksburg%2C%20VA%2024060"
                    target="_blank"
                    rel="noopener"
                    style={{
                      display: 'inline-block',
                      background: MAROON,
                      color: '#fff',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: 600,
                      padding: '11px 20px',
                      borderRadius: '9px',
                      border: `1px solid ${MAROON}`,
                    }}
                  >
                    Open in Google Maps
                  </a>
                </div>
              </div>
            )}

            {mapState.status === 'loading' && (
              <div style={mapFallback} aria-live="polite">
                <div style={{ maxWidth: '360px' }}>
                  <div style={{ fontFamily: EXP, fontWeight: 700, fontSize: '18px', color: INK, marginBottom: '8px' }}>
                    Loading map…
                  </div>
                  <p style={{ fontSize: '14.5px', color: '#5C5853', lineHeight: 1.5, margin: 0 }}>
                    One moment while we fetch the location.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      </main>

      {/* Shared site footer */}
      <LandingFooter
        tagline="Harnessing the power of peptides to advance equine health."
        columns={[
          {
            heading: 'Products',
            links: [
              { label: 'All products', href: '/products' },
              { label: 'BIO-PLY™', href: '/products/bio-ply' },
            ],
          },
          {
            heading: 'Company',
            links: [
              { label: 'About', href: '/#about' },
              { label: 'Our Team', href: '/about/team' },
              { label: 'Send us a message', href: '#form' },
            ],
          },
          {
            heading: 'Support',
            links: [
              { label: 'Login', href: '/login' },
              { label: 'Request access', href: '#request-access' },
            ],
          },
        ]}
      />
    </div>
  )
}

const eyebrow = (color) => ({ fontSize: '12.5px', letterSpacing: '.18em', textTransform: 'uppercase', color, fontWeight: 700, marginBottom: '18px' })
const labelStyle = { display: 'block', fontSize: '13px', color: '#4A4744', marginBottom: '7px', fontWeight: 600 }
const fieldStyle = {
  width: '100%', padding: '13px 15px', background: '#FAF9F7', border: '1px solid #E4DFD7',
  borderRadius: '9px', color: '#1A1917', fontFamily: "'Archivo',sans-serif", fontSize: '15px', transition: 'border-color .25s',
}
const mapFallback = {
  position: 'absolute',
  inset: 0,
  display: 'grid',
  placeItems: 'center',
  textAlign: 'center',
  padding: 'clamp(24px,4vw,40px)',
  background: '#EDE8E1',
}
